import React, { ReactNode, useEffect, useState } from 'react';

import { CheckOutlined, SearchOutlined } from '@ant-design/icons/lib';
import classNames from 'classnames';
import styled from 'styled-components';

import Button from './Button';
import Input from './Input';
import Select from './Select';

const StyledSelect = styled(Select)`
  &.ant-select {
    font-family: var(--font-regular);
    font-size: 12px;
    height: 28px;
    color: inherit;
    color: var(--deep-blue);
    background-color: var(--lighter-gray);
    border-radius: 4px;

    &.active {
      color: var(--dark-blue);
      background-color: var(--faded-blue);
    }

    &.ant-select-focused {
      color: var(--dark-blue);
      background-color: var(--faded-blue);
      border: 1px solid var(--button-blue);
    }

    .ant-select-arrow {
      color: inherit;
      width: 16px;
      height: 16px;
      top: 12px;
    }
    .ant-select-arrow .anticon {
      width: 8px;
      height: 4.9px;
      margin: auto;
    }
    .ant-select-selector {
      color: inherit;
      .ant-select-selection-placeholder {
        color: inherit;
        font-size: 12px;
      }
      .ant-select-selection-item {
        color: inherit;
        font-size: 12px;
      }
    }
  }
`;

const Label = styled.span`
  font-family: var(--font-bold);
`;

const LabelBadge = styled.span`
  font-family: var(--font-bold);
  font-size: 10px;

  background-color: var(--positive-green);
  border-radius: 4px;
  padding: 3px 2px;
  margin-left: 4px;

  & {
    color: white;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 20px 10px 20px;
  gap: 10px;
`;

const Selection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const SelectionActions = styled.div`
  display: flex;
  gap: 10px;

  span {
    color: var(--dark-blue);
    font-size: 12px;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }

    &.disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--line-gray);
  max-height: 200px;
  overflow-y: auto;
  padding: 10px 20px;
`;

const ItemsTitle = styled.span`
  font-family: var(--font-bold);
  margin-bottom: 15px;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  cursor: pointer;
  gap: 10px;

  &.padded {
    padding-left: 24px;
  }
`;

const ItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  .selected-visible {
    display: none;
  }

  &.selected {
    .selected-visible {
      display: flex;
    }
  }
`;

const ColorDot = styled.span`
  color: ${(props) => props.color};
  margin-right: 8px;
`;

interface Props {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  options: any[];
  otherOptions?: ReactNode;
  otherOptionsLabel?: string;
  defaultValue: any[];
  onSelect: any;
  dataTestId?: string;
}

const SelectAdvancedFilter = ({
  className,
  disabled = false,
  options,
  otherOptions,
  otherOptionsLabel,
  defaultValue,
  placeholder,
  onSelect,
  dataTestId,
}: Props) => {
  const [open, setOpen] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [nonSelectedItems, setNonSelectedItems] = useState<any[]>([]);

  const [filteredSelectedItems, setFilteredSelectedItems] = useState<any[]>([]);
  const [filteredNonSelectedItems, setFilteredNonSelectedItems] = useState<any[]>([]);

  useEffect(() => {
    if (options?.length > 0) {
      setSelectedItems([...options.filter((item) => defaultValue.includes(item.value))]);
      setNonSelectedItems([...options.filter((item) => !defaultValue.includes(item.value))]);
    }
  }, [open, options, defaultValue]);

  useEffect(() => {
    const filteredSelected = selectedItems.filter((item) =>
      item.label?.toLowerCase().includes(searchKeyword?.toLowerCase())
    );
    const filteredNonSelected = nonSelectedItems.filter((item) =>
      item.label?.toLowerCase().includes(searchKeyword?.toLowerCase())
    );

    setFilteredSelectedItems(filteredSelected);
    setFilteredNonSelectedItems(filteredNonSelected);
  }, [searchKeyword, selectedItems, nonSelectedItems]);

  const handleSelectItem = (selectedItem: any) => {
    setSelectedItems((prev) => [...prev, selectedItem]);
    setNonSelectedItems((prev) => [...prev.filter((item: any) => item.value !== selectedItem.value)]);
  };

  const handleDeselectItem = (selectedItem: any) => {
    setSelectedItems((prev) => {
      const newSelection = [...prev.filter((item: any) => item.value !== selectedItem.value)];
      setNonSelectedItems([...options?.filter((item: any) => !newSelection.includes(item))]);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    setSelectedItems([...options]);
    setNonSelectedItems([]);
  };

  const handleSelectNone = () => {
    setSelectedItems([]);
    setNonSelectedItems([...options]);
  };

  const handleSearch = (e: any) => {
    setSearchKeyword(e.target.value);
  };

  const handleApplySelection = () => {
    onSelect(selectedItems);
    setOpen(false);
  };

  const placeholderInfo =
    selectedItems?.length > 0
      ? `${placeholder}:`
      : !otherOptionsLabel
        ? `${placeholder}`
        : `${placeholder} (${otherOptionsLabel})`;

  return (
    <StyledSelect
      open={open}
      className={className}
      variant={'borderless'}
      onDropdownVisibleChange={(visible) => setOpen(visible)}
      disabled={disabled}
      placeholder={placeholderInfo}
      value={defaultValue}
      dataTestId={dataTestId}
      labelRender={({ label, value }) => {
        let labelInfo = '';

        if (selectedItems.length > 0) {
          labelInfo = selectedItems[0].label;
        }
        if (otherOptionsLabel) {
          labelInfo = `${labelInfo} (${otherOptionsLabel})`;
        }

        return (
          <>
            <Label>{placeholderInfo} </Label>
            {labelInfo}
            {selectedItems.length > 1 && <LabelBadge>+{selectedItems.length - 1}</LabelBadge>}
          </>
        );
      }}
      dropdownStyle={{
        padding: 0,
        borderRadius: 0,
        border: '1px solid var(--line-gray)',
        minWidth: 370,
      }}
      dropdownRender={(menu) => {
        return (
          <Container>
            <Controls>
              <Selection>
                <SelectionActions>
                  <span
                    onClick={handleSelectAll}
                    className={classNames({ disabled: nonSelectedItems.length === 0 })}
                    data-test-id={dataTestId ? `${dataTestId}-select-all` : undefined}
                  >
                    Select All
                  </span>
                  <span
                    onClick={handleSelectNone}
                    className={classNames({ disabled: selectedItems.length === 0 })}
                    data-test-id={dataTestId ? `${dataTestId}-select-none` : undefined}
                  >
                    Select None
                  </span>
                </SelectionActions>
                <Button
                  type={'primary'}
                  onClick={handleApplySelection}
                  style={{ width: 96, height: 36 }}
                  data-test-id={dataTestId ? `${dataTestId}-apply` : undefined}
                >
                  Apply
                </Button>
              </Selection>
              {options?.length > 6 && (
                <Input
                  placeholder={`Search ${placeholder?.toLowerCase() ?? ''}`}
                  prefix={<SearchOutlined />}
                  onChange={handleSearch}
                  data-test-id={dataTestId ? `${dataTestId}-search` : undefined}
                />
              )}
            </Controls>
            {otherOptions}
            {filteredSelectedItems.length > 0 && (
              <DropdownItemsList
                title={'SELECTED'}
                items={filteredSelectedItems}
                className={'selected'}
                icon={<CheckOutlined size={18} style={{ color: 'var(--positive-green)' }} />}
                onSelect={handleDeselectItem}
                dataTestIdPrefix={dataTestId}
                groupName="selected"
              />
            )}
            {filteredNonSelectedItems.length > 0 && (
              <DropdownItemsList
                title={'NOT SELECTED'}
                items={filteredNonSelectedItems}
                onSelect={handleSelectItem}
                dataTestIdPrefix={dataTestId}
                groupName="option"
              />
            )}
          </Container>
        );
      }}
    />
  );
};

interface PropsList {
  className?: string;
  title: string;
  items: any[];
  icon?: ReactNode;
  onSelect: any;
  dataTestIdPrefix?: string;
  groupName?: string;
}

const DropdownItemsList = ({ className, title, items, icon, onSelect, dataTestIdPrefix, groupName }: PropsList) => (
  <Items className={'mini-scroll'}>
    <ItemsTitle>{title}</ItemsTitle>
    <ItemsList>
      {items.map((item) => (
        <Item
          key={item.value}
          className={classNames({ padded: !icon })}
          onClick={() => onSelect(item)}
          data-test-id={
            dataTestIdPrefix ? `${dataTestIdPrefix}-${groupName}-${String(item.value ?? 'item')}` : undefined
          }
        >
          {icon}
          <ItemContent className={className}>
            <div>
              {item?.color && <ColorDot color={item.color}>●</ColorDot>}
              {item.label && <span>{item.label}</span>}
            </div>
            {item.component}
          </ItemContent>
        </Item>
      ))}
    </ItemsList>
  </Items>
);

export default SelectAdvancedFilter;
